import React, { FC, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import useDebounce from "@/hooks/useDebounce";
import { MODULE_NAME, OPTIONS, ACTION_CODE } from "@/domain/auditlog.enum";
import {
  useAuditLogFirstRender,
  useCreateAuditlog,
} from "@/hooks/useAuditLogs";
import { useUserStore } from "@/hooks/useUserStore";
import DataTable, { createDataRow, HeadCell } from "@/components/DataTable";
import TabPanel from "@/components/TabPanel";
import { usePageStore } from "@/hooks/usePageStore";
import AuditLogModal from "@/ui/modals/AuditLogModal";
import { useClientFilters } from "@/providers/ClientsFiltersContext";
import ParticipantFilters from "@/ui/content/participants/Filters";
import ParticipantRegisterModal from "@/ui/modals/participants/ParticipantRegisterModal";
import {
  useParticipantSearch,
  useSoftDeleteParticipant,
} from "@/hooks/useParticipants";
import ParticipantUpdateModal from "@/ui/modals/participants/ParticipantUpdateModal";
import OnDemandValidationRequestModal from "@/ui/modals/OnDemandValidationRequestModal";
import { Participant } from "@/domain/participant.interface";
import { useCountrySetting } from "@/hooks/useSetting";
import { AlertType, useAlertModal } from "@/hooks/useAlertModal";
import AlertDialog from "@/components/AlertDialog";
import { PermissionDeniedModal } from "@/components/PermissionDeniedModal/PermissionDeniedModal";
import theme from "@/utils/theme";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { Order } from "@/components/DataTable/dataTable.interface";
import {
  Button,
  Chip,
  IconButton,
  Paper,
  Tooltip,
  useMediaQuery,
  Tabs,
  Tab,
  Menu,
  MenuItem,
} from "@mui/material";
import { formatParticipantStatus } from "@/services/utils/participantStatus.utils";
import { formatTestResultStatus } from "@/services/utils/testResultStatus.utils";
import {
  formatEnrollmentStatus,
  getEnrollmentStatusColor,
} from "@/services/utils/enrollmentStatus.utils";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CameraAltOutlined from "@mui/icons-material/CameraAltOutlined";

const ParticipantPage: FC = () => {
  const navigate = useNavigate();
  const { userInfo, userHasAccessToCode } = useUserStore();
  const createAuditLog = useCreateAuditlog();
  const { setPageTitle } = usePageStore();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [openAuditLogModal, setOpenAuditLogModal] = useState(false);
  const [showValidationRequestModal, setShowValidationRequestModal] =
    useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);
  const countrySetting = useCountrySetting();
  const country = useMemo(() => countrySetting, [countrySetting]);
  const { showSnackBar } = useFeedBack();

  const { alertState, closeAlert, confirmAction } = useAlertModal();
  const [showDeleteModal, setShowDeleteModal] = useState({
    open: false,
    participant: {} as Participant | null,
  });
  const [showPermissionDeniedModal, setShowPermissionDeniedModal] =
    useState(false);

  const { mutateAsync: softDeleteParticipant } = useSoftDeleteParticipant();

  const [selectedParticipant] = useState<Participant>({} as Participant);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenAuditLog = () => {
    setOpenAuditLogModal(true);
    handleMenuClose();
  };

  const handleValidationRequest = () => {
    if (!userHasAccessToCode("PARTICIPANTS_ONDEMAND_VALIDATION_REQUEST")) {
      setShowPermissionDeniedModal(true);
      handleMenuClose();
      return;
    }
    setShowValidationRequestModal(true);
    handleMenuClose();
  };

  const { filters, setFilters } = useClientFilters();
  const [searchParams] = useSearchParams();

  // COYN-915: Restore filters from URL when navigating back from profile
  useEffect(() => {
    const program = searchParams.get("program");
    const notEnrolled = searchParams.get("notEnrolled");
    const orgUnitId = searchParams.get("orgUnitId");
    const groupLabelId = searchParams.get("groupLabelId");
    const status = searchParams.get("status");
    const search = searchParams.get("search");
    const sortField = searchParams.get("sortField");
    const sortOrder = searchParams.get("sortOrder");
    const pageNumber = searchParams.get("pageNumber");
    const pageSize = searchParams.get("pageSize");

    const hasParams =
      program !== null ||
      notEnrolled !== null ||
      orgUnitId !== null ||
      groupLabelId !== null ||
      status !== null ||
      search !== null ||
      sortField !== null ||
      sortOrder !== null ||
      pageNumber !== null ||
      pageSize !== null;

    if (hasParams) {
      setFilters((prev) => ({
        ...prev,
        ...(program !== null && { program }),
        ...(notEnrolled !== null && {
          notEnrolled: notEnrolled === "true",
        }),
        ...(orgUnitId !== null && { orgUnitId }),
        ...(groupLabelId !== null && { groupLabelId }),
        ...(status !== null && { status: status.split(",").filter(Boolean) }),
        ...(search !== null && { search }),
        ...(sortField !== null && { sortField }),
        ...(sortOrder !== null && { sortOrder }),
        ...(pageNumber !== null && { pageNumber: parseInt(pageNumber, 10) || 0 }),
        ...(pageSize !== null && { pageSize: parseInt(pageSize, 10) || 10 }),
      }));
    }
  }, [searchParams, setFilters]);

  const debouncedInputValue = useDebounce({ value: filters.search });
  const showCompactView = isMobile || filters.notEnrolled;

  const { data, refetchParticipants, isFetchingParticipants } =
    useParticipantSearch({
      search: debouncedInputValue,
      notEnrolled: filters.notEnrolled,
      programIds:
        filters.program !== "all"
          ? [filters.program]
          : filters.assignedPrograms,
      orgUnitId: filters.orgUnitId
        ? filters.orgUnitId
        : (filters.assignedOrgUnits || []).join(","),
      groupLabelId: filters.groupLabelId,
      status: filters.status.length > 0 ? filters.status : undefined,
      pageNumber: filters.pageNumber,
      pageSize: filters.pageSize,
      sortField: filters.sortField,
      sortOrder: filters.sortOrder,
    });

  const rows = useMemo(() => {
    if (!data || (data && data.participants.length === 0)) return [];

    return (
      data?.participants.map((participant) => {
        const rowData: Record<string, React.ReactNode> = {};
        rowData.firstName = (
          <Typography variant="bodySmall">
            {participant.firstName} {participant.lastName}
          </Typography>
        );
        if (!showCompactView) {
          rowData.cmStartDate = (
            <Typography variant="bodyMedium">
              {participant.cmStartDate || "N/A"}
            </Typography>
          );
        }
        if (!showCompactView) {
          rowData.registrationStage = (
            <Typography variant="bodyMedium">
              {formatParticipantStatus(participant.registrationStage)}
            </Typography>
          );
        }
        if (!showCompactView) {
          rowData.firstValidation = (
            <Typography variant="bodyMedium">
              {formatTestResultStatus(participant.firstValidation)}
            </Typography>
          );
        }
        if (!showCompactView) {
          rowData.status = (
            <Chip
              label={
                <Typography variant="labelSmall">
                  {formatEnrollmentStatus(participant.status)}
                </Typography>
              }
              size="medium"
              sx={{
                ...getEnrollmentStatusColor(participant.status),
                borderWidth: 1,
                borderStyle: "solid",
              }}
            />
          );
        }
        rowData.action = (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 1,
            }}
          >
            <ChevronRightIcon
              sx={{ color: "icon.main", fontSize: "1.25rem" }}
            />
          </Box>
        );
        return createDataRow(participant.id, rowData);
      }) || []
    );
  }, [data]);

  const allHeadCells: HeadCell[] = [
    {
      id: "firstName",
      label: "Full name",
      isSortable: true,
      disablePadding: false,
      align: "left",
    },
    {
      id: "cmStartDate",
      label: "CM start date",
      isSortable: true,
      disablePadding: false,
      align: "left",
    },
    {
      id: "registrationStage",
      label: "Registration stage",
      isSortable: true,
      disablePadding: false,
      align: "left",
    },
    {
      id: "firstValidation",
      label: "First validation",
      isSortable: true,
      disablePadding: false,
      align: "left",
    },
    {
      id: "status",
      label: "Status",
      isSortable: false,
      disablePadding: false,
      align: "left",
    },
    {
      id: "action",
      label: "",
      isSortable: false,
      disablePadding: false,
      align: "right",
    },
  ];

  const headCells = showCompactView
    ? allHeadCells.filter(
        (cell) => cell.id === "firstName" || cell.id === "action",
      )
    : allHeadCells;

  useEffect(() => {
    createAuditLog.mutate({
      appType: "WEB_BACK_OFFICE",
      module: MODULE_NAME.PARTICIPANTS,
      option: OPTIONS.PARTICIPANTS_LIST,
      actionCode: ACTION_CODE.WEB_PART_LIST_SEARCH,
      action: "When searching",
      detail: "Searched within the list of participants",
      transactionDate: new Date(),
      accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
      createdBy: userInfo?._id as string,
      name: `${userInfo?.firstName} ${userInfo?.lastName}`,
    });

    refetchParticipants();
  }, [debouncedInputValue]);

  useAuditLogFirstRender({
    appType: "WEB_BACK_OFFICE",
    module: MODULE_NAME.PARTICIPANTS,
    option: OPTIONS.PARTICIPANTS_LIST,
    actionCode: ACTION_CODE.WEB_PART_LIST_VIEW,
    action: "When entering",
    detail: "Viewed the list of participants",
    transactionDate: new Date(),
    accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
    createdBy: userInfo?._id as string,
    name: `${userInfo?.firstName} ${userInfo?.lastName}`,
  });

  useEffect(() => {
    setPageTitle("");
  }, []);

  const handleDelete = async () => {
    if (!showDeleteModal.participant) return;
    await softDeleteParticipant(showDeleteModal.participant._id, {
      onSuccess: () => {
        showSnackBar("Participant deleted successfully");
        setShowDeleteModal({ open: false, participant: null });
        refetchParticipants();
      },
    });
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* Top Bar */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1.6,
        }}
      >
        <Typography variant="headlineMedium">Participants List</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Actions">
            <IconButton
              size="small"
              onClick={handleMenuClick}
              sx={{
                border: "1px solid",
                borderColor: "primary.main",
                borderRadius: 1,
                "&:hover": {
                  borderColor: "primary.light",
                  backgroundColor: "rgba(98, 83, 162, 0.08)",
                },
                minHeight: "2.5rem",
                height: "2.5rem",
                width: "2.5rem",
                minWidth: "2.5rem",
              }}
            >
              <MoreVertIcon
                sx={{ color: "primary.main", fontSize: "1.25rem" }}
              />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            open={openMenu}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            PaperProps={{
              sx: {
                maxHeight: "200px",
                minWidth: "220px",
                mt: 5,
              },
            }}
          >
            <MenuItem
              onClick={handleValidationRequest}
              sx={{
                fontSize: "14px",
                gap: 1,
                py: 1.5,
              }}
            >
              <CameraAltOutlined
                sx={{ color: "#261F1F", fontSize: "1.25rem" }}
              />
              On demand request validation
            </MenuItem>
            <MenuItem
              onClick={handleOpenAuditLog}
              sx={{
                fontSize: "14px",
                gap: 1,
                py: 1.5,
              }}
            >
              <HistoryOutlinedIcon
                sx={{ color: "#261F1F", fontSize: "1.25rem" }}
              />
              Audit logs
            </MenuItem>
          </Menu>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => {
              if (!userHasAccessToCode("PARTICIPANT_ADD")) {
                setShowPermissionDeniedModal(true);
                return;
              }
              return setShowRegisterModal(true);
            }}
            sx={{
              minHeight: "2.5rem",
              textTransform: "none",
              height: "2.5rem",
              textAlign: "center",
              fontSize: "13px",
              fontWeight: 500,
              borderRadius: "8px",
              padding: "0.5rem 1rem",
            }}
          >
            ADD
          </Button>
        </Box>
      </Box>
      <Paper sx={{ width: "100%", p: { xs: 1.5, sm: 2 } }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs value={0} aria-label="participants tabs">
            <Tab
              label="In Programs"
              sx={{
                textTransform: "uppercase",
                fontWeight: 500,
                fontSize: "14px",
                minHeight: "48px",
                "&.Mui-selected": {
                  color: "primary.main",
                },
              }}
            />
          </Tabs>
        </Box>
        <TabPanel value={0} index={0}>
          <ParticipantFilters
            filtersState={[filters, setFilters]}
            isLoadingButtons={false}
          />
          <DataTable
            data={rows}
            headCells={headCells}
            order={filters.sortOrder as Order}
            orderBy={filters.sortField}
            rowsPerPage={filters.pageSize}
            page={filters.pageNumber}
            total={data?.totalCount || 0}
            isLoading={isFetchingParticipants}
            onChangePage={(event, newPage) => {
              setFilters((filters) => ({
                ...filters,
                pageNumber: newPage,
              }));
            }}
            onChangeSize={(event) => {
              setFilters((filters) => ({
                ...filters,
                pageNumber: 0,
                pageSize: Number(event.target.value),
              }));
            }}
            onChangeSort={(order, orderBy) => {
              setFilters((filters) => ({
                ...filters,
                pageNumber: 0,
                sortField: orderBy as string,
                sortOrder: order,
              }));
            }}
            onRowClick={(event, id) => {
              navigate(`/participants/${id}`);
            }}
            headCellFontSize="14px"
            emptyDataFontSize="14px"
          />
        </TabPanel>
      </Paper>

      {showRegisterModal ? (
        <ParticipantRegisterModal
          open={showRegisterModal}
          onClose={() => {
            setShowRegisterModal(false);
            refetchParticipants();
          }}
        />
      ) : null}

      {showUpdateModal ? (
        <ParticipantUpdateModal
          open={showUpdateModal}
          participant={selectedParticipant}
          onClose={() => {
            setShowUpdateModal(false);
            refetchParticipants();
          }}
          country={country}
        />
      ) : null}

      {showDeleteModal.open && (
        <AlertDialog
          open={showDeleteModal.open}
          title="Delete"
          description="Are you sure you want to delete this registry?"
          onCancel={() => {
            setShowDeleteModal({ open: false, participant: null });
          }}
          onConfirm={handleDelete}
          confirmText="Confirm"
          cancelText="Cancel"
        />
      )}

      {alertState.open && (
        <AlertDialog
          open={alertState.open}
          onCancel={
            alertState.alertType === AlertType.CONFIRMATION
              ? closeAlert
              : closeAlert
          }
          onConfirm={confirmAction}
          title={alertState.title}
          description={alertState.message}
          confirmText={
            alertState.alertType === AlertType.CONFIRMATION
              ? "Confirm"
              : "Accept"
          }
          cancelText={
            alertState.alertType === AlertType.CONFIRMATION
              ? "Cancel"
              : undefined
          }
        />
      )}

      {openAuditLogModal ? (
        <AuditLogModal
          open={openAuditLogModal}
          module={MODULE_NAME.PARTICIPANTS}
          option={OPTIONS.PARTICIPANTS_LIST}
          onClose={() => setOpenAuditLogModal(false)}
        />
      ) : null}

      <PermissionDeniedModal
        open={showPermissionDeniedModal}
        onClose={() => setShowPermissionDeniedModal(false)}
      />

      {showValidationRequestModal ? (
        <OnDemandValidationRequestModal
          open={showValidationRequestModal}
          onClose={() => setShowValidationRequestModal(false)}
        />
      ) : null}
    </Box>
  );
};

export default ParticipantPage;
